import { BKFC_RECAPTCHA_SITE_KEY } from "./config";

// Ported from the Aptos reference app (apps/web/src/lib/recaptcha.ts).
//
// Google Cloud reCAPTCHA Enterprise, not classic reCAPTCHA v3. Geomi's
// reCAPTCHA panel asks for a Project ID, Site Key, and API Key — that
// combination only exists on the Enterprise product. Client-side API is
// grecaptcha.enterprise, a different global than classic reCAPTCHA.
//
// action must match whatever action name is set on the Geomi gas station's
// reCAPTCHA rule, otherwise Geomi's own verification rejects a token that's
// otherwise perfectly valid. Keep this in sync with the dashboard by hand.
const RECAPTCHA_ACTION = "claim_certificate";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string },
        ) => Promise<string>;
      };
    };
  }
}

let scriptLoadPromise: Promise<void> | null = null;

function loadScript(siteKey: string): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    // no integrity/SRI hash here on purpose: Google serves this file
    // dynamically per site key, there's no fixed hash to pin
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load reCAPTCHA Enterprise script"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

function fetchToken(): Promise<string | undefined> {
  const siteKey = BKFC_RECAPTCHA_SITE_KEY;
  if (!siteKey) return Promise.resolve(undefined);

  return loadScript(siteKey).then(
    () =>
      new Promise<string>((resolve, reject) => {
        const grecaptcha = window.grecaptcha;
        if (!grecaptcha) {
          reject(
            new Error(
              "reCAPTCHA Enterprise script loaded but window.grecaptcha is missing",
            ),
          );
          return;
        }
        grecaptcha.enterprise.ready(() => {
          grecaptcha.enterprise
            .execute(siteKey, { action: RECAPTCHA_ACTION })
            .then(resolve)
            .catch(reject);
        });
      }),
  );
}

let tokenPromise: Promise<string | undefined> | null = null;

// Generates a token ahead of the click that will consume it, not inside the
// click handler. Aptos Connect's confirm prompt is a window.open() call,
// which browsers only allow within a short window after a genuine click;
// running this at click time silently killed that popup in the reference
// project. Call on mount, well before the user clicks.
export function warmRecaptchaToken(): void {
  tokenPromise = fetchToken();
  // swallow so an unused warm-up doesn't log an unhandled rejection;
  // getRecaptchaToken() still surfaces the real error to whoever awaits it
  tokenPromise.catch(() => {});
}

// Waits on whatever warmRecaptchaToken() already started, normally already
// resolved by the time this is called.
export async function getRecaptchaToken(): Promise<string | undefined> {
  if (!tokenPromise) warmRecaptchaToken();
  return tokenPromise!;
}
